import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import {
  PageTitle, Spinner, Empty, DataCard, Icon, Modal, Th, Td, Button, Field, Input, Select, cx
} from "../../components/ui";
import { LABEL, urutkanDetail } from "../attendance/todoDetail";

// ─── Pemantauan Trainer — masa pengawasan & kepatuhan To-Do List ─────────────
//
// Management memasukkan trainer tertentu ke masa pengawasan (per orang, semua cabang).
// Selama itu To-Do List wajib diisi di setiap kelas bermurid yang ia ajar; trainer lain
// tak melihat to-do sama sekali. Semua hitungan dari server (pengawasan.controller) —
// halaman ini tak menghitung ulang apa pun selain persentase dan sisa hari.
//
// Tanggal "hari ini" diambil dari SERVER (jam Jakarta), bukan jam perangkat: aturan
// "mulai tak boleh sebelum hari ini" ditegakkan di server, dan formulir yang memakai jam
// perangkat yang meleset akan menawarkan tanggal yang pasti ditolak.

const HARI = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const BLN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const BARIS_PER_HALAMAN = 100;

const keTanggal = (iso) => { const [y, m, d] = String(iso || "").split("-").map(Number); return new Date(y, m - 1, d); };
const keIso = (t) =>
  `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
const geserHari = (iso, n) => { const t = keTanggal(iso); t.setDate(t.getDate() + n); return keIso(t); };
// "1 bulan" mulai 13 Okt = sampai 12 Nov: periodenya genap sebulan, hari mulai ikut dihitung.
const geserBulan = (iso, n) => { const t = keTanggal(iso); t.setMonth(t.getMonth() + n); t.setDate(t.getDate() - 1); return keIso(t); };
const selisihHari = (a, b) => Math.round((keTanggal(b) - keTanggal(a)) / 86400000);

function tanggalBaca(iso, pakaiTahun = true) {
  const t = keTanggal(iso);
  if (Number.isNaN(t.getTime())) return iso || "";
  return `${t.getDate()} ${BLN[t.getMonth()]}${pakaiTahun ? ` ${t.getFullYear()}` : ""}`;
}
const tanggalHari = (iso) => `${HARI[keTanggal(iso).getDay()]}, ${tanggalBaca(iso, false)}`;
const periodeTeks = (p) => `${tanggalBaca(p.mulai, false)} – ${tanggalBaca(p.selesai)}`;

const persen = (a, b) => (b > 0 ? Math.round((a / b) * 100) : null);

const SARINGAN = [
  ["semua", "Semua"],
  ["tidak-diisi", "Tidak diisi"],
  ["sebagian", "Sebagian"],
  ["lengkap", "Lengkap"],
  ["tak-wajib", "Tak wajib"]
];
const takWajib = (s) => s.status === "tanpa-murid" || s.status === "tidak-berlaku";

const ALASAN = {
  "program-kosong": "Program kelas belum diisi — tak ada bank aktivitas",
  "program-tanpa-bank": "Program ini belum punya bank aktivitas (mis. Weekend, TOEFL)"
};

export default function PemantauanTrainerPage({ setToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);          // { mode: "tambah" | "ubah", periode? }
  const [rincianId, setRincianId] = useState(null);
  const [rincian, setRincian] = useState(null);
  const [memuatRincian, setMemuatRincian] = useState(false);
  const [lihatRiwayat, setLihatRiwayat] = useState(false);
  const [isi, setIsi] = useState(null);            // { sesi, memuat, body }

  const muat = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.getPengawasan());
    } catch (err) {
      setData(null);
      setToast && setToast({ type: "error", message: err.message || "Gagal memuat Pemantauan Trainer" });
    } finally {
      setLoading(false);
    }
  }, [setToast]);

  useEffect(() => { muat(); }, [muat]);

  const muatRincian = useCallback(async (id) => {
    setMemuatRincian(true);
    try {
      setRincian(await api.getRincianPengawasan(id));
    } catch (err) {
      setRincian(null);
      setToast && setToast({ type: "error", message: err.message || "Gagal memuat rincian" });
    } finally {
      setMemuatRincian(false);
    }
  }, [setToast]);

  function bukaRincian(id) {
    if (rincianId === id) { setRincianId(null); setRincian(null); return; }
    setRincianId(id);
    muatRincian(id);
  }

  async function akhiri(p) {
    const tanya = p.status === "berjalan"
      ? `Akhiri masa pengawasan ${p.nama} sekarang? Mulai hari ini to-do tak lagi wajib dan tombol Simpan-nya tak terkunci.`
      : `Batalkan masa pengawasan ${p.nama} yang belum dimulai?`;
    if (!window.confirm(tanya)) return;
    try {
      const r = await api.akhiriPengawasan(p.id);
      setToast && setToast({ type: "success", message: r.message || "Masa pengawasan diakhiri." });
      await muat();
      if (rincianId === p.id) muatRincian(p.id);
    } catch (err) {
      setToast && setToast({ type: "error", message: err.message || "Gagal mengakhiri" });
    }
  }

  async function simpan(payload) {
    try {
      const r = form.mode === "tambah"
        ? await api.tambahPengawasan(payload)
        : await api.ubahPengawasan(form.periode.id, payload);
      setToast && setToast({ type: "success", message: r.message || "Tersimpan." });
      setForm(null);
      await muat();
      if (form.mode === "ubah" && rincianId === form.periode.id) muatRincian(form.periode.id);
    } catch (err) {
      // Formulir tetap terbuka: penolakan server (mis. periode bertumpuk) harus bisa
      // langsung dibetulkan, bukan diisi ulang dari awal.
      setToast && setToast({ type: "error", message: err.message || "Gagal menyimpan" });
    }
  }

  const periode = data?.periode || [];
  const berjalan = periode.filter((p) => p.status === "berjalan");
  const akanDatang = periode.filter((p) => p.status === "akan-datang");
  const riwayat = periode.filter((p) => p.status === "selesai" || p.status === "dibatalkan");
  const dipilih = periode.find((p) => p.id === rincianId) || null;

  return (
    <>
      <PageTitle
        title="Pemantauan Trainer"
        subtitle="Trainer dalam masa pengawasan wajib mengisi To-Do List di setiap kelas yang ia ajar, di cabang mana pun."
        action={
          <Button onClick={() => setForm({ mode: "tambah" })} disabled={!data?.success}>
            <Icon name="person_add" /> Tambah ke pengawasan
          </Button>
        }
      />

      {loading ? (
        <Spinner label="Memuat Pemantauan Trainer..." />
      ) : !data?.success ? (
        <Empty label="Pemantauan Trainer belum bisa dimuat." />
      ) : (
        <div className="space-y-8">
          <Bagian judul="Sedang diawasi" ikon="visibility" jumlah={berjalan.length}>
            {berjalan.length === 0 ? (
              <Empty label="Belum ada trainer yang sedang diawasi." />
            ) : (
              <TabelPeriode
                daftar={berjalan}
                hariIni={data.hariIni}
                rincianId={rincianId}
                onRincian={bukaRincian}
                onUbah={(p) => setForm({ mode: "ubah", periode: p })}
                onAkhiri={akhiri}
              />
            )}
          </Bagian>

          {akanDatang.length > 0 && (
            <Bagian judul="Akan datang" ikon="event_upcoming" jumlah={akanDatang.length}>
              <TabelPeriode
                daftar={akanDatang}
                hariIni={data.hariIni}
                onUbah={(p) => setForm({ mode: "ubah", periode: p })}
                onAkhiri={akhiri}
              />
            </Bagian>
          )}

          {rincianId != null && (
            <Rincian
              key={rincianId}
              periode={dipilih}
              memuat={memuatRincian}
              data={rincian}
              tutup={() => { setRincianId(null); setRincian(null); }}
              bukaIsi={async (s) => {
                setIsi({ sesi: s, memuat: true, body: null });
                try {
                  setIsi({ sesi: s, memuat: false, body: await api.getTodoIsi(s.todo.classId, s.tanggal) });
                } catch (err) {
                  setIsi(null);
                  setToast && setToast({ type: "error", message: err.message || "Gagal memuat isi to-do" });
                }
              }}
            />
          )}

          {riwayat.length > 0 && (
            <section>
              <button
                type="button"
                onClick={() => setLihatRiwayat((x) => !x)}
                className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-primary"
              >
                <Icon name={lihatRiwayat ? "expand_less" : "expand_more"} />
                {lihatRiwayat ? "Sembunyikan riwayat" : `Tampilkan riwayat (${riwayat.length})`}
              </button>
              {lihatRiwayat && (
                <div className="mt-3">
                  <TabelPeriode
                    daftar={riwayat}
                    hariIni={data.hariIni}
                    rincianId={rincianId}
                    onRincian={bukaRincian}
                  />
                </div>
              )}
            </section>
          )}
        </div>
      )}

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.mode === "ubah" ? "Ubah masa pengawasan" : "Tambah ke pengawasan"}
      >
        {form && data?.success && (
          <FormPengawasan
            key={form.mode + (form.periode?.id || "")}
            mode={form.mode}
            periode={form.periode}
            hariIni={data.hariIni}
            trainer={data.trainer || []}
            onSimpan={simpan}
            onBatal={() => setForm(null)}
          />
        )}
      </Modal>

      <Modal open={!!isi} onClose={() => setIsi(null)} title="Isi To-Do List" wide>
        {isi && <IsiTodo isi={isi} />}
      </Modal>
    </>
  );
}

function Bagian({ judul, ikon, jumlah, children }) {
  return (
    <section>
      <h3 className="mb-3 flex items-center gap-2 text-lg font-black">
        <Icon name={ikon} className="text-primary" /> {judul}
        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">{jumlah}</span>
      </h3>
      {children}
    </section>
  );
}

function Kepatuhan({ r }) {
  if (!r || r.wajib === 0) {
    return <span className="text-xs text-slate-400">Belum ada sesi wajib</span>;
  }
  const p = persen(r.lengkap, r.wajib);
  return (
    <div className="min-w-[180px]">
      <div className="flex items-baseline justify-between gap-2">
        <span
          className={cx("text-sm font-black", p >= 80 ? "text-green-600" : p >= 40 ? "text-amber-500" : "text-red-500")}
        >
          {p}% lengkap
        </span>
        <span className="text-[11px] text-slate-400">{r.lengkap}/{r.wajib} sesi</span>
      </div>
      <div className="mt-1 flex h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div className="bg-green-500" style={{ width: `${(r.lengkap / r.wajib) * 100}%` }} />
        <div className="bg-amber-400" style={{ width: `${(r.sebagian / r.wajib) * 100}%` }} />
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        {r.sebagian} sebagian · {r.tidakDiisi} tidak diisi
        {r.tanpaMurid + r.tidakBerlaku > 0 ? ` · ${r.tanpaMurid + r.tidakBerlaku} tak wajib` : ""}
      </p>
      {r.tahapManual > 0 && (
        <p className="text-[11px] text-violet-600 dark:text-violet-400">
          {r.tahapManual} dari {r.tahapAktivitas} tahap ditulis sendiri
        </p>
      )}
    </div>
  );
}

function TabelPeriode({ daftar, hariIni, rincianId, onRincian, onUbah, onAkhiri }) {
  return (
    <DataCard>
      <table className="w-full text-left">
        <thead>
          <tr>
            <Th>Trainer</Th>
            <Th>Periode</Th>
            <Th>Kepatuhan To-Do</Th>
            <Th right> </Th>
          </tr>
        </thead>
        <tbody>
          {daftar.map((p) => (
            <tr
              key={p.id}
              className={cx("border-t border-slate-100 align-top dark:border-slate-800", p.id === rincianId && "bg-primary/5")}
            >
              <Td>
                <p className="font-black text-slate-800 dark:text-slate-100">{p.nama}</p>
                <p className="text-[11px] text-slate-400">{(p.cabang || []).join(", ") || "—"}</p>
                {p.catatan && (
                  <p className="mt-1 max-w-xs truncate text-[11px] italic text-slate-500" title={p.catatan}>
                    Catatan: {p.catatan}
                  </p>
                )}
              </Td>
              <Td className="whitespace-nowrap">
                <p className="font-bold">{periodeTeks(p)}</p>
                <p className="text-[11px] text-slate-500"><Keadaan p={p} hariIni={hariIni} /></p>
              </Td>
              <Td>
                {p.status === "akan-datang" || p.status === "dibatalkan"
                  ? <span className="text-xs text-slate-400">—</span>
                  : <Kepatuhan r={p.ringkas} />}
              </Td>
              <Td right className="whitespace-nowrap">
                <div className="flex justify-end gap-1">
                  {onRincian && p.status !== "dibatalkan" && (
                    <TombolKecil onClick={() => onRincian(p.id)} label={`${p.id === rincianId ? "Tutup" : "Rincian"} ${p.nama}`}>
                      {p.id === rincianId ? "Tutup" : "Rincian"}
                    </TombolKecil>
                  )}
                  {onUbah && (
                    <TombolKecil onClick={() => onUbah(p)} label={`Ubah ${p.nama}`}>Ubah</TombolKecil>
                  )}
                  {onAkhiri && (
                    <TombolKecil onClick={() => onAkhiri(p)} label={`${p.status === "berjalan" ? "Akhiri" : "Batalkan"} ${p.nama}`} bahaya>
                      {p.status === "berjalan" ? "Akhiri" : "Batalkan"}
                    </TombolKecil>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </DataCard>
  );
}

function Keadaan({ p, hariIni }) {
  if (p.status === "berjalan") {
    const sisa = selisihHari(hariIni, p.selesai);
    return sisa === 0 ? "hari terakhir" : `sisa ${sisa + 1} hari`;
  }
  if (p.status === "akan-datang") return `mulai dalam ${selisihHari(hariIni, p.mulai)} hari`;
  if (p.status === "dibatalkan") {
    return <span className="font-bold text-slate-400">Dibatalkan{p.diakhiriOleh ? ` oleh ${p.diakhiriOleh}` : ""}</span>;
  }
  return p.diakhiriOleh ? `Diakhiri lebih cepat oleh ${p.diakhiriOleh}` : "Selesai";
}

function TombolKecil({ children, onClick, label, bahaya = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cx(
        "rounded-lg px-2 py-1 text-xs font-bold",
        bahaya ? "text-red-600 hover:bg-red-500/10" : "text-primary hover:bg-primary/10"
      )}
    >
      {children}
    </button>
  );
}

function FormPengawasan({ mode, periode, hariIni, trainer, onSimpan, onBatal }) {
  const ubah = mode === "ubah";
  const [userId, setUserId] = useState(ubah ? String(periode.userId) : "");
  const [cari, setCari] = useState("");
  const [mulai, setMulai] = useState(ubah ? periode.mulai : hariIni);
  const [selesai, setSelesai] = useState(ubah ? periode.selesai : "");
  const [catatan, setCatatan] = useState(ubah ? periode.catatan || "" : "");
  const [menyimpan, setMenyimpan] = useState(false);

  // Pengawasan yang sudah berjalan tak bisa digeser mulainya (server juga menolak).
  const mulaiTerkunci = ubah && periode.status !== "akan-datang";
  const pilihan = useMemo(() => {
    const q = cari.trim().toLowerCase();
    return trainer.filter((t) => !q || t.nama.toLowerCase().includes(q) || (t.cabang || []).join(" ").toLowerCase().includes(q));
  }, [trainer, cari]);

  const minSelesai = mulai > hariIni ? mulai : hariIni;
  const sah = !!userId && !!mulai && !!selesai && selesai >= mulai && (mulaiTerkunci || mulai >= hariIni);

  async function kirim(e) {
    e.preventDefault();
    if (!sah || menyimpan) return;
    setMenyimpan(true);
    await onSimpan(ubah ? { mulai, selesai, catatan } : { userId: Number(userId), mulai, selesai, catatan });
    setMenyimpan(false);
  }

  return (
    <form onSubmit={kirim} className="space-y-4">
      {ubah ? (
        <p className="text-sm">
          <span className="font-black">{periode.nama}</span>
          <span className="text-slate-400"> · {(periode.cabang || []).join(", ")}</span>
        </p>
      ) : (
        <>
          <Field label="Cari trainer">
            <Input value={cari} onChange={(e) => setCari(e.target.value)} placeholder="Nama atau kode cabang…" />
          </Field>
          <Field label="Trainer">
            <Select value={userId} onChange={(e) => setUserId(e.target.value)} required>
              <option value="">— pilih trainer —</option>
              {pilihan.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nama}{t.cabang?.length ? ` — ${t.cabang.join(", ")}` : ""}
                  {t.diawasi === "berjalan" ? " (sedang diawasi)" : t.diawasi === "akan-datang" ? " (akan diawasi)" : ""}
                </option>
              ))}
            </Select>
          </Field>
        </>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Tanggal mulai">
          <Input
            type="date"
            value={mulai}
            min={mulaiTerkunci ? undefined : hariIni}
            disabled={mulaiTerkunci}
            onChange={(e) => setMulai(e.target.value)}
            required
          />
        </Field>
        <Field label="Tanggal selesai">
          <Input type="date" value={selesai} min={minSelesai} onChange={(e) => setSelesai(e.target.value)} required />
        </Field>
      </div>
      <div className="flex flex-wrap gap-2">
        {[["2 minggu", geserHari(mulai || hariIni, 13)], ["1 bulan", geserBulan(mulai || hariIni, 1)],
          ["3 bulan", geserBulan(mulai || hariIni, 3)]].map(([label, nilai]) => (
          <button
            key={label}
            type="button"
            onClick={() => setSelesai(nilai < minSelesai ? minSelesai : nilai)}
            className="rounded-full border border-slate-200 px-3 py-1 text-xs font-bold text-slate-500 hover:border-primary/40 hover:text-primary dark:border-slate-700"
          >
            {label}
          </button>
        ))}
      </div>
      {mulaiTerkunci && (
        <p className="text-xs text-slate-500">
          Tanggal mulai tak bisa diubah karena pengawasannya sudah berjalan. Untuk menghentikannya sekarang, pakai Akhiri.
        </p>
      )}

      <Field label="Catatan untuk management">
        <textarea
          value={catatan}
          onChange={(e) => setCatatan(e.target.value)}
          maxLength={500}
          rows={3}
          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
      </Field>
      <p className="text-[11px] text-slate-500">
        Catatan hanya terlihat oleh management — tidak ditampilkan ke trainer. Trainer hanya melihat periodenya.
      </p>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
        Selama periode ini trainer tersebut wajib mengisi To-Do List di setiap kelas bermurid yang ia ajar, di
        cabang mana pun. Tombol Simpan absensinya terkunci sampai ketujuh tahap berisi aktivitas.
        &quot;Selesai Tanpa Murid&quot; tidak dikunci.
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onBatal}>Batal</Button>
        <Button type="submit" disabled={!sah || menyimpan}>{menyimpan ? "Menyimpan…" : "Simpan"}</Button>
      </div>
    </form>
  );
}

function Rincian({ periode, memuat, data, tutup, bukaIsi }) {
  const [saring, setSaring] = useState("semua");
  const [batas, setBatas] = useState(BARIS_PER_HALAMAN);
  const sesi = useMemo(() => data?.sesi || [], [data]);
  const hitung = useMemo(() => {
    const n = { semua: sesi.length, "tidak-diisi": 0, sebagian: 0, lengkap: 0, "tak-wajib": 0 };
    sesi.forEach((s) => { if (takWajib(s)) n["tak-wajib"]++; else n[s.status] = (n[s.status] || 0) + 1; });
    return n;
  }, [sesi]);
  const tersaring = sesi.filter((s) =>
    saring === "semua" || (saring === "tak-wajib" ? takWajib(s) : s.status === saring));
  const tahapTotal = data?.tahapTotal || 7;

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-lg font-black">
          <Icon name="list_alt" className="text-primary" />
          Rincian {periode ? periode.nama : ""}
          {periode && <span className="text-sm font-medium text-slate-400">· {periodeTeks(periode)}</span>}
        </h3>
        <TombolKecil onClick={tutup} label="Tutup rincian">Tutup</TombolKecil>
      </div>

      {memuat ? (
        <Spinner label="Memuat rincian..." />
      ) : !data?.success ? (
        <Empty label="Rincian belum bisa dimuat." />
      ) : sesi.length === 0 ? (
        <Empty label="Belum ada sesi mengajar di dalam periode ini." />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Saring status">
            {SARINGAN.map(([nilai, label]) => (
              <button
                key={nilai}
                type="button"
                onClick={() => { setSaring(nilai); setBatas(BARIS_PER_HALAMAN); }}
                aria-pressed={saring === nilai}
                className={cx(
                  "rounded-full border px-3 py-1 text-xs font-bold",
                  saring === nilai
                    ? "border-primary bg-primary text-white"
                    : "border-slate-200 text-slate-500 hover:border-primary/40 dark:border-slate-700"
                )}
              >
                {label} ({hitung[nilai] || 0})
              </button>
            ))}
          </div>

          {tersaring.length === 0 ? (
            <Empty label="Tidak ada sesi untuk saringan ini." />
          ) : (
            <DataCard>
              <table className="w-full text-left">
                <thead>
                  <tr>
                    <Th>Tanggal</Th>
                    <Th>Sesi</Th>
                    <Th>Cabang</Th>
                    <Th>Kelas</Th>
                    <Th right>To-Do</Th>
                  </tr>
                </thead>
                <tbody>
                  {tersaring.slice(0, batas).map((s) => (
                    <tr key={`${s.tanggal}|${s.cabang}|${s.sesi}`} className="border-t border-slate-100 align-top dark:border-slate-800">
                      <Td className="whitespace-nowrap font-bold">{tanggalHari(s.tanggal)}</Td>
                      <Td className="whitespace-nowrap">{s.sesi}</Td>
                      <Td>{s.cabang}</Td>
                      <Td>
                        {s.kelas.map((k) => (
                          <div key={k.id} className="text-xs">
                            <span>{k.nama}</span>
                            {k.pergantian && (
                              <span className="ml-1.5 rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-bold text-violet-600 dark:text-violet-400">
                                {k.pergantian === "gabung" ? `digabung dari ${k.pemilik}` : `menggantikan ${k.pemilik}`}
                              </span>
                            )}
                          </div>
                        ))}
                      </Td>
                      <Td right className="whitespace-nowrap">
                        <StatusSesi sesi={s} tahapTotal={tahapTotal} bukaIsi={bukaIsi} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tersaring.length > batas && (
                <div className="border-t border-slate-100 p-3 text-center dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => setBatas((n) => n + BARIS_PER_HALAMAN)}
                    className="text-xs font-bold text-primary hover:underline"
                  >
                    Tampilkan {Math.min(BARIS_PER_HALAMAN, tersaring.length - batas)} lagi
                  </button>
                </div>
              )}
            </DataCard>
          )}
        </>
      )}
    </section>
  );
}

function StatusSesi({ sesi, tahapTotal, bukaIsi }) {
  if (sesi.status === "lengkap" || sesi.status === "sebagian") {
    const lengkap = sesi.status === "lengkap";
    return (
      <>
      <button
        type="button"
        onClick={() => bukaIsi(sesi)}
        className={cx(
          "rounded-full px-3 py-1 text-xs font-black",
          lengkap
            ? "bg-green-500/15 text-green-700 hover:bg-green-500/25 dark:text-green-400"
            : "bg-amber-400/20 text-amber-700 hover:bg-amber-400/30 dark:text-amber-300"
        )}
        title={`Diisi ${sesi.todo.pengisi || "—"} · ${sesi.todo.diubah || ""} — klik untuk membaca isinya`}
      >
        {lengkap ? "Lengkap" : "Sebagian"} {sesi.todo.tahap}/{tahapTotal} · Baca isi
      </button>
      {sesi.todo.manual > 0 && (
        <p className="mt-1 text-[10px] text-violet-600 dark:text-violet-400">{sesi.todo.manual} tulisan sendiri</p>
      )}
      </>
    );
  }
  if (takWajib(sesi)) {
    return (
      <span
        className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 dark:bg-slate-800"
        title={sesi.status === "tanpa-murid" ? "Tak ada murid hadir — Selesai Tanpa Murid tak wajib to-do" : ALASAN[sesi.alasan] || ""}
      >
        {sesi.status === "tanpa-murid" ? "Tanpa murid" : "Tidak berlaku"}
      </span>
    );
  }
  return (
    <span className="rounded-full bg-red-500/10 px-3 py-1 text-xs font-black text-red-600 dark:text-red-400">
      Tidak diisi
    </span>
  );
}

// Isian yang ditulis trainer sendiri, bukan dipilih dari bank — tak punya "cara menjalankan"
// dari bank, jadi keterangan trainerlah satu-satunya penjelasannya.
function TandaSendiri() {
  return (
    <span className="ml-1.5 rounded-full bg-violet-500/10 px-2 py-0.5 text-[10px] font-black text-violet-600 dark:text-violet-400">
      tulisan sendiri
    </span>
  );
}

// Menit dari isian waktu bebas ("10 min", "5′", "15", "1 jam"). null = tak terbaca.
function menit(waktu) {
  const t = String(waktu || "").trim().toLowerCase();
  const m = /(\d+(?:[.,]\d+)?)/.exec(t);
  if (!m) return null;
  const n = Number(m[1].replace(",", "."));
  return /jam|hour|hr/.test(t) ? Math.round(n * 60) : Math.round(n);
}

function IsiTodo({ isi }) {
  // Bawaannya LENGKAP: management membuka ini justru untuk tahu bagaimana kelasnya
  // dijalankan, bukan cuma nama aktivitasnya. "Ringkas" untuk membaca sekilas.
  const [lengkap, setLengkap] = useState(true);
  if (isi.memuat) return <Spinner label="Memuat isi to-do..." />;
  const { body, sesi } = isi;
  const kelas = body?.kelas || {};
  const aktivitas = body?.aktivitas || {};
  const deskripsi = body?.deskripsi || {};
  const items = new Map(((body?.data && body.data.items) || []).map((i) => [i.tahap, i]));
  const tahap = body?.tahap || [];
  const beraktivitas = tahap.filter((t) => items.get(t)?.activity).length;
  const tulisanSendiri = tahap.filter((t) => items.get(t)?.activity && items.get(t)?.activityManual).length;
  const menitTerbaca = [...items.values()].map((i) => menit(i.waktu)).filter((x) => x != null);
  const totalMenit = menitTerbaca.reduce((a, b) => a + b, 0);
  const hadir = body?.hadir;

  return (
    <div>
      <div className="mb-4 grid gap-1 text-sm text-slate-600 dark:text-slate-300">
        <p className="font-black text-slate-800 dark:text-slate-100">{kelas.nama}</p>
        <p>
          {kelas.cabang} · {kelas.sesi} · {kelas.program || "program belum diisi"} · {tanggalHari(sesi.tanggal)}
        </p>
        <p className="text-xs text-slate-500">
          Diisi {body?.data?.trainer || sesi.todo.pengisi || "—"}
          {sesi.todo.diubah ? ` · terakhir diubah ${sesi.todo.diubah}` : ""}
        </p>
      </div>

      {!body?.data ? (
        <Empty label="To-do untuk kelas & tanggal ini tidak ditemukan." />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
            {hadir && (
              <span className="rounded-full bg-slate-100 px-3 py-1 font-bold dark:bg-slate-800">
                {hadir.murid + hadir.tambahan} murid hadir
                {hadir.tambahan ? ` (${hadir.tambahan} murid tambahan)` : ""}
              </span>
            )}
            <span className="rounded-full bg-slate-100 px-3 py-1 font-bold dark:bg-slate-800">
              {beraktivitas}/{tahap.length} tahap beraktivitas
            </span>
            {tulisanSendiri > 0 && (
              <span className="rounded-full bg-violet-500/10 px-3 py-1 font-bold text-violet-600 dark:text-violet-400">
                {tulisanSendiri} tahap tulisan sendiri
              </span>
            )}
            {menitTerbaca.length > 0 && (
              <span className="rounded-full bg-slate-100 px-3 py-1 font-bold dark:bg-slate-800">
                Rencana waktu ± {totalMenit} menit
              </span>
            )}
            <button
              type="button"
              onClick={() => setLengkap((x) => !x)}
              className="ml-auto rounded-full border border-slate-200 px-3 py-1 font-bold text-primary hover:bg-primary/10 dark:border-slate-700"
            >
              {lengkap ? "Ringkas" : "Tampilkan cara menjalankan"}
            </button>
          </div>

          <ol className="space-y-3">
            {tahap.map((t, n) => {
              const i = items.get(t);
              const isiAda = i && (i.activity || i.bonding || i.waktu || i.doFlag);
              const detailAkt = i && i.activityId ? aktivitas[i.activityId] : null;
              return (
                <li key={t} className="rounded-xl border border-slate-200 p-3 dark:border-slate-800">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-black">{n + 1}. {t}</p>
                    <div className="flex items-center gap-1.5">
                      {i?.waktu && (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-black text-primary">{i.waktu}</span>
                      )}
                      {i?.doFlag && (
                        <span
                          className={cx("rounded-full px-2 py-0.5 text-[10px] font-black text-white", i.doFlag === "Do" ? "bg-green-500" : "bg-red-500")}
                        >
                          {i.doFlag === "Do" ? "Do · dilakukan" : "Don't · dihindari"}
                        </span>
                      )}
                    </div>
                  </div>
                  {lengkap && deskripsi[t] && (
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-400">{deskripsi[t]}</p>
                  )}

                  {isiAda ? (
                    <dl className="mt-2 grid gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                      <div>
                        <dt className="inline font-bold">Aktivitas: </dt>
                        <dd className="inline font-bold text-slate-800 dark:text-slate-100">
                          {i.activity || <em className="font-normal text-amber-600">belum dipilih</em>}
                          {i.activity && i.activityManual && <TandaSendiri />}
                        </dd>
                      </div>
                      {i.activity && i.activityManual && (
                        <div className="rounded-lg border border-violet-200 bg-violet-50 p-2 dark:border-violet-500/20 dark:bg-violet-500/10">
                          <dt className="text-[10px] font-black uppercase tracking-wide text-violet-600 dark:text-violet-400">
                            Keterangan trainer
                          </dt>
                          <dd className="whitespace-pre-line">
                            {i.keterangan || <em className="text-slate-400">Tanpa keterangan.</em>}
                          </dd>
                        </div>
                      )}
                      {i.bonding && (
                        <div>
                          <dt className="font-bold">
                            Act of bonding yang dipilih:{i.bondingManual && <TandaSendiri />}
                          </dt>
                          <dd className="whitespace-pre-line">{i.bonding}</dd>
                        </div>
                      )}
                    </dl>
                  ) : (
                    <p className="mt-1 text-xs italic text-slate-400">Tahap ini belum diisi.</p>
                  )}

                  {lengkap && detailAkt && (
                    <div className="mt-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
                      <p className="mb-1.5 text-[11px] font-black uppercase tracking-wide text-primary">
                        Cara menjalankan {detailAkt.nama}
                      </p>
                      {detailAkt.videoUrl && (
                        <a
                          href={detailAkt.videoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mb-2 inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-xs font-black text-primary hover:underline"
                        >
                          <Icon name="play_circle" className="text-sm" />
                          Buka video contoh
                        </a>
                      )}
                      <dl className="space-y-1.5">
                        {urutkanDetail(detailAkt.detail || {}).map(([k, v]) => (
                          <div key={k}>
                            <dt className="text-[10px] font-black uppercase tracking-wide text-slate-400">{LABEL[k] || k}</dt>
                            <dd className="whitespace-pre-line text-xs text-slate-600 dark:text-slate-300">{v}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
}
